import { AnimationBuilder, AnimationFactory, AnimationQueryMetadata, animate, group, keyframes, query, style } from '@angular/animations';
import { ElementRef, Injectable } from '@angular/core';

import { isShallowCopy } from '@frontend/sports/common/core/utils/collection';
import { ReplaySubject, Subject } from 'rxjs';

export interface CarouselSizing {
    lastItemScale: 'min' | 'scaled' | 'max';
}

interface CarouselState {
    offsetInPx: number;
    lastItemWidth: number;
}

@Injectable()
export class CarouselAnimationService {
    private done = new Subject<void>();
    private sizing = new ReplaySubject<CarouselSizing>(1);

    private initialized = false;
    private animationFactory: AnimationFactory;
    private itemWidth: number;
    private scrollingWidth: number;
    private hostWidth: number;
    private state: CarouselState;
    get currentState(): CarouselState {
        return this.state;
    }

    readonly done$ = this.done.asObservable();
    readonly sizing$ = this.sizing.asObservable();

    get isInitialized(): boolean {
        return this.initialized;
    }

    constructor(
        private host: ElementRef,
        private animationBuilder: AnimationBuilder,
    ) {}

    init(itemWidth: number, itemsCount: number): void {
        this.hostWidth = this.host.nativeElement.offsetWidth;
        this.itemWidth = itemWidth;
        this.scrollingWidth = itemsCount * this.itemWidth;

        this.state = {
            lastItemWidth: itemWidth,
            offsetInPx: 0,
        };

        this.initialized = true;
    }

    animate(offset: number, skipAnimation: boolean = false): void {
        const newState = this.calculateState(offset);

        if (isShallowCopy(newState, this.state)) {
            this.onAnimationDone(skipAnimation);

            return;
        }

        this._animate(newState, skipAnimation);
    }

    private _animate(newState: CarouselState, skipAnimation: boolean): void {
        if (!this.animationFactory) {
            this.animationFactory = this.getAnimationFactory();
        }

        const params = this.getAnimationsParams(newState, skipAnimation);
        const player = this.animationFactory.create(this.host.nativeElement, { params });

        player.onDone(() => this.onAnimationDone(skipAnimation));
        player.play();

        this.state = newState;
    }

    private calculateState(offset: number): CarouselState {
        const offsetInPx = offset * this.itemWidth;
        let lastItemWidth: number = this.itemWidth;

        if (this.scrollingWidth < offsetInPx + this.hostWidth) {
            lastItemWidth = lastItemWidth + (offsetInPx + this.hostWidth) - this.scrollingWidth;
        } else if (this.hostWidth > this.scrollingWidth) {
            lastItemWidth = lastItemWidth + this.hostWidth - this.scrollingWidth;
        }

        return {
            lastItemWidth,
            offsetInPx,
        };
    }

    private onAnimationDone(skipAnimation: boolean): void {
        this.onSizingChange();

        !skipAnimation && this.done.next();
    }

    private onSizingChange(): void {
        const getLastItemScale = (value: number) => {
            if (value === this.itemWidth) {
                return 'min';
            }

            if (value === this.hostWidth) {
                return 'max';
            }

            return 'scaled';
        };

        this.sizing.next({
            lastItemScale: getLastItemScale(this.state.lastItemWidth),
        });
    }

    private getAnimationsParams(newState: CarouselState, skipAnimation: boolean): any {
        return {
            scrollerTiming: skipAnimation ? '0s' : '300ms ease-in-out',
            lastItemTiming: skipAnimation ? '0s' : '230ms 50ms',
            offset: -this.state.offsetInPx,
            newOffset: -newState.offsetInPx,
            width: this.state.lastItemWidth,
            newWidth: newState.lastItemWidth,
        };
    }

    private getAnimationFactory(): AnimationFactory {
        return this.animationBuilder.build([group([this.getOffsetAnimation(), this.getScaleAnimation()])]);
    }

    private getOffsetAnimation(): AnimationQueryMetadata {
        return query('.acca-boost-carousel-scroller', [
            animate(
                '{{ scrollerTiming }}',
                keyframes([style({ transform: `translateX({{offset}}px)` }), style({ transform: `translateX({{newOffset}}px)` })]),
            ),
        ]);
    }

    private getScaleAnimation(): AnimationQueryMetadata {
        return query('.carousel-item:last-child', [
            animate('{{ lastItemTiming }}', keyframes([style({ width: `{{width}}px` }), style({ width: `{{newWidth}}px` })])),
        ]);
    }
}
